import React from 'react';
import styles from './Blocks.module.css';
import { IconChevronRight } from './Icons';

/* ══════════════════════════════════════════════════════════════════
 * InfoGrid — 2열 정보 표
 *
 * 모임 상세의 dash-info-grid(날짜|시간, 주최자|카테고리), 정산 요약,
 * 일정 결과 등에서 같은 형태가 반복됩니다.
 * ════════════════════════════════════════════════════════════════ */

export interface InfoItem {
    /** 항목 이름 — i18n 으로 넘깁니다 */
    label: string;
    /** 값. 미정이면 muted 로 흐리게 */
    value: React.ReactNode;
    muted?: boolean;
    /** 한 줄 전체를 차지 (초대코드처럼 긴 값) */
    full?: boolean;
}

interface InfoGridProps {
    items: InfoItem[];
    /** 1열로 세울지 (기본 2열) */
    columns?: 1 | 2;
}

export const InfoGrid = ({ items, columns = 2 }: InfoGridProps) => (
    <dl className={columns === 1 ? `${styles.infoGrid} ${styles.infoGrid1}` : styles.infoGrid}>
        {items.map((it, i) => (
            <div
                key={`${it.label}-${i}`}
                className={styles.infoCell}
                style={it.full && columns === 2 ? { gridColumn: '1 / -1', borderRight: 0 } : undefined}
            >
                <dt className={styles.infoLabel}>{it.label}</dt>
                <dd className={it.muted ? `${styles.infoValue} ${styles.infoValueMuted}` : styles.infoValue}>
                    {it.value}
                </dd>
            </div>
        ))}
    </dl>
);

/* ══════════════════════════════════════════════════════════════════
 * CopyRow — 값 + 복사 (초대코드 · 공유 링크 · 계좌번호)
 * ════════════════════════════════════════════════════════════════ */

const IcCopy = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect x="9" y="9" width="12" height="12" rx="2" />
        <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
    </svg>
);

interface CopyRowProps {
    label: string;
    value: string;
    /** 버튼 라벨 — i18n (예: common.copy) */
    copyLabel: string;
    onCopy: () => void;
}

export const CopyRow = ({ label, value, copyLabel, onCopy }: CopyRowProps) => (
    <div className={styles.copyRow}>
        <span className={styles.copyBody}>
            <span className={styles.copyLabel}>{label}</span>
            <span className={styles.copyValue}>{value}</span>
        </span>
        <button type="button" className={styles.copyBtn} onClick={onCopy}>
            <IcCopy />
            {copyLabel}
        </button>
    </div>
);

/* ══════════════════════════════════════════════════════════════════
 * MapBlock — 지도 자리
 *
 * 실제 화면에서는 children 으로 기존 <MapContainer/> 를 그대로 넣습니다.
 * 여기서는 지도 위에 얹히는 상태 배지·장소 이름·액션의 배치만 규정합니다.
 * ════════════════════════════════════════════════════════════════ */

interface MapBlockProps {
    /** 실제 지도 컴포넌트. 없으면 회색 자리표시자 */
    children?: React.ReactNode;
    /** 좌상단 상태 배지 */
    badge?: React.ReactNode;
    /** 좌하단 장소 이름 */
    placeName?: string;
    /** 우하단 액션 (외부 지도 열기 등) */
    actions?: React.ReactNode;
    onClick?: () => void;
    ariaLabel?: string;
}

export const MapBlock = ({
    children, badge, placeName, actions, onClick, ariaLabel,
}: MapBlockProps) => (
    <button type="button" className={styles.mapBlock} onClick={onClick} aria-label={ariaLabel}>
        {children}
        {badge ? <span className={styles.mapBadge}>{badge}</span> : null}
        {(placeName || actions) ? (
            <span className={styles.mapOverlay}>
                {placeName ? <span className={styles.mapName}>{placeName}</span> : <span />}
                {actions}
            </span>
        ) : null}
    </button>
);

/* ══════════════════════════════════════════════════════════════════
 * DangerZone — 되돌릴 수 없는 동작을 화면 맨 아래 한 곳에 모읍니다
 * ════════════════════════════════════════════════════════════════ */

interface DangerZoneProps {
    /** 영역 이름 — i18n (예: "이 모임에서") */
    title?: string;
    children: React.ReactNode;
}

export const DangerZone = ({ title, children }: DangerZoneProps) => (
    <div className={styles.danger}>
        {title ? <p className={styles.dangerTitle}>{title}</p> : null}
        <div className={styles.dangerRow}>{children}</div>
    </div>
);

/* ══════════════════════════════════════════════════════════════════
 * ActionRow / ActionList — 누르면 이동하는 줄
 *
 * 참여 방법 선택, 설정 메뉴, 기능 목록에서 같은 형태가 반복됩니다.
 * ════════════════════════════════════════════════════════════════ */


interface ActionRowProps {
    /** 좌측 인라인 SVG 아이콘 */
    icon?: React.ReactNode;
    title: string;
    desc?: string;
    /** 우측에 보여 줄 현재값 (언어 설정 등) */
    value?: string;
    onClick: () => void;
    /** 목록이 아니라 단독으로 놓을 때 위에 선을 하나 더 */
    standalone?: boolean;
    /** 되돌릴 수 없는 동작 — 글자를 경고색으로 */
    danger?: boolean;
    /** 화살표 숨김 (같은 화면에서 처리되는 동작) */
    noArrow?: boolean;
}

export const ActionRow = ({
    icon, title, desc, value, onClick, standalone, danger, noArrow,
}: ActionRowProps) => (
    <button
        type="button"
        className={[
            styles.actionRow,
            standalone ? styles.actionRowStandalone : '',
            danger ? styles.actionRowDanger : '',
        ].filter(Boolean).join(' ')}
        onClick={onClick}
    >
        {icon ? <span className={styles.actionIcon}>{icon}</span> : null}
        <span className={styles.actionBody}>
            <span className={styles.actionTitle}>{title}</span>
            {desc ? <span className={styles.actionDesc}>{desc}</span> : null}
        </span>
        {value ? <span className={styles.actionValue}>{value}</span> : null}
        {noArrow ? null : <span className={styles.actionArrow}><IconChevronRight /></span>}
    </button>
);

export const ActionList = ({ children }: { children: React.ReactNode }) => (
    <div className={styles.actionList}>{children}</div>
);

/** 가운데 글자가 들어간 구분선 — 라벨은 i18n 으로 넘깁니다 (예: "또는") */
export const DividerText = ({ label }: { label: string }) => (
    <div className={styles.dividerText}>
        <span className={styles.dividerTextLabel}>{label}</span>
    </div>
);

/** 화면 상단 제목 + 설명 */
export const PageIntro = ({ title, desc }: { title: React.ReactNode; desc?: React.ReactNode }) => (
    <div className={styles.intro}>
        <h1 className={styles.introTitle}>{title}</h1>
        {desc ? <p className={styles.introDesc}>{desc}</p> : null}
    </div>
);
